import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  ChevronLeft,
  Play,
  NotebookPen,
  Printer,
  Users,
  CheckCircle2,
  Save,
  Coins,
  PiggyBank,
  RotateCcw
} from 'lucide-react';
import { useLab, studentBase, getCourseTemplateForChild, resolveLabChildId } from '../LabContext';
import { LabLayout, Card, NimnyArt, ParentPreviewBadge } from '../LabUI';
import { findDay, computeDayStatus, getWeeksForCourse } from '../data/lessons';
import { formatNims, buildLessonKey } from '../utils/nims';

// MVP single lesson view that contains all 4 modes as in-page tabs.
const Lesson = () => {
  const { weekId, dayId, studentId: routeStudentId } = useParams();
  const navigate = useNavigate();
  const {
    t,
    tx,
    user,
    language,
    childProgress,
    updateLessonResponse,
    setNotebookDone,
    completeLesson,
    resetLesson,
    awardBaseNimsForLesson,
    awardBonusNimForLesson,
    hasBaseNimAward,
    hasBonusNimAward,
    getLessonNimAward,
    getNimBalance,
    refreshBackendStudent
  } = useLab();
  const location = useLocation();
  const childId = resolveLabChildId({
    routeStudentId,
    pathname: location.pathname,
    user,
  });
  const base = studentBase(childId);
  const courseTemplate = getCourseTemplateForChild(user, childId);
  const weeks = getWeeksForCourse(courseTemplate);

  const day = findDay(weekId, dayId, weeks);
  const progress = childProgress(childId);
  const status = computeDayStatus(progress, weekId, dayId, weeks);
  const record = progress[`${weekId}/${dayId}`] || {
    responses: {},
    notebookDone: false
  };

  // Honour ?tab=parent on first mount so the parent-preview "Гид родителя"
  // link from My Path opens the parent answer-key tab directly.
  const initialTab = (() => {
    const sp = new URLSearchParams(location.search || '');
    const t = sp.get('tab');
    if (t === 'parent' || t === 'notebook' || t === 'online') return t;
    return 'online';
  })();
  const [tab, setTab] = useState(initialTab);
  const [savedFlash, setSavedFlash] = useState(false);
  const [resetBusy, setResetBusy] = useState(false);
  const [completeBusy, setCompleteBusy] = useState(false);

  const isParent = user?.role === 'parent' || user?.role === 'super_admin';

  // Parent preview: pull the freshest progress + nims for THIS child from the
  // backend on mount, so the lesson reflects what the child has actually
  // completed and bonus controls compute totals correctly.
  useEffect(() => {
    if (!isParent || !childId) return;
    refreshBackendStudent(childId).catch(() => { /* ignore */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isParent, childId, weekId, dayId]);

  // If child tries to enter a locked lesson, kick them back to /path.
  // Parents / super-admins can preview any lesson regardless of progress.
  useEffect(() => {
    if (isParent) return;
    if (!day || day.placeholder || status === 'locked') {
      navigate(`${base}/path`, { replace: true });
    }
  }, [day, status, navigate, childId, isParent, base]);

  // Auto-save flash indicator when responses change
  useEffect(() => {
    if (Object.keys(record.responses || {}).length === 0) return;
    setSavedFlash(true);
    const timer = setTimeout(() => setSavedFlash(false), 1200);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(record.responses)]);

  if (!day || day.placeholder) return null;

  const handleAnswer = (qid, value) =>
    updateLessonResponse(childId, weekId, dayId, qid, value);

  const handleComplete = async () => {
    setCompleteBusy(true);
    try {
      const resp = await completeLesson(childId, weekId, dayId);
      // Promise resolves to null only on backend failure (network / 403).
      // For child-self path or local-only fallback, resp may be undefined —
      // those paths succeed without a return value.
      if (resp === null) {
        window.alert(language === 'ru'
          ? 'Не удалось завершить день. Проверь, что ты вошёл как родитель и есть интернет.'
          : 'Could not complete the day. Check that you are signed in as a parent and have internet.');
      }
    } finally {
      setCompleteBusy(false);
    }
  };

  const handleReset = async () => {
    const msg = language === 'ru'
      ? 'Сбросить день? Награда за завершение (базовые нимы) будет списана. Родительские бонусы за этот урок остаются.'
      : 'Reset this day? The base completion nims will be revoked. Parent bonuses for this lesson are kept.';
    if (!window.confirm(msg)) return;
    setResetBusy(true);
    try {
      const resp = await resetLesson(childId, weekId, dayId);
      if (!resp) {
        window.alert(language === 'ru'
          ? 'Не удалось сбросить день. Проверь подключение и роль (нужен родитель).'
          : 'Could not reset the day. Check connection and that you are signed in as a parent.');
      }
    } finally {
      setResetBusy(false);
    }
  };

  const notebookSteps =
    day.notebook?.steps?.[language] || day.notebook?.steps?.ru || [];
  const parentPrompts =
    day.parentGuide?.prompts?.[language] || day.parentGuide?.prompts?.ru || [];

  return (
    <LabLayout desktopWide>
      <Link
        to={isParent ? '/lab/parent' : `${base}/path`}
        className="inline-flex items-center gap-1.5 text-amber-200/75 hover:text-amber-200 text-sm font-sans"
        data-testid="lesson-back"
      >
        <ChevronLeft className="w-4 h-4" />
        {t('back')}
      </Link>

      {/* Parent preview indicator (only renders for parent / super_admin) */}
      <ParentPreviewBadge />

      <div className="mt-2 lg:grid lg:grid-cols-12 lg:gap-6 lg:items-start">
        {/* ----- Main column ----- */}
        <div className="lg:col-span-8 space-y-3">

      {/* Header */}
      <section className="mt-4 text-center">
        <p
          className="text-amber-200/60 text-sm"
          style={{ fontFamily: 'var(--font-heading)' }}
        >
          {t('weekLabel')} {weekId.split('-')[1]} · {t('dayLabel')} {dayId.split('-')[1]}
        </p>
        <h1
          className="mt-1 text-3xl text-[#f3d9a4] leading-tight"
          style={{ fontFamily: 'var(--font-heading)' }}
          data-testid="lesson-title"
        >
          {tx(day.title)}
        </h1>
        {day.subtitle && (
          <p className="mt-1 text-amber-200/65 font-serif text-sm">
            {tx(day.subtitle)}
          </p>
        )}
        {day.estimatedTimeLabel && (
          <p className="mt-3 inline-flex items-center gap-1.5 text-amber-200/55 text-xs font-sans px-3 py-1 rounded-full border border-amber-300/15">
            ⏳ {tx(day.estimatedTimeLabel)}
          </p>
        )}
      </section>

      {/* Nimny intro */}
      <Card className="mt-5 p-5 flex gap-4 items-start">
        <NimnyArt size="sm" />
        <div className="flex-1 text-amber-100/85 font-serif text-sm leading-relaxed">
          <div className="text-amber-200/60 text-xs uppercase tracking-wider font-sans mb-1.5">
            {t('lessonIntro')}
          </div>
          <p>{tx(day.intro)}</p>
        </div>
      </Card>

      {/* Optional: lesson goals */}
      {Array.isArray(day.goals?.[language]) && day.goals[language].length > 0 && (
        <Card className="mt-3 p-5" data-testid="lesson-goals">
          <h3 className="text-amber-200/75 text-xs uppercase tracking-wider font-sans mb-2">
            {t('goalsLabel')}
          </h3>
          <ul className="space-y-1.5 list-disc list-inside text-amber-100/85 font-serif text-sm">
            {day.goals[language].map((g, i) => (
              <li key={i}>{g}</li>
            ))}
          </ul>
        </Card>
      )}

      {/* Optional: vocabulary — moved to right sidebar on desktop, rendered
          inline on mobile below the hero. Component output is identical; the
          sidebar render is the one that survives on lg+ (this inline copy
          shows only below lg). */}
      {Array.isArray(day.vocabulary) && day.vocabulary.length > 0 && (
        <Card className="mt-3 p-5 lg:hidden" data-testid="lesson-vocabulary">
          <h3 className="text-amber-200/75 text-xs uppercase tracking-wider font-sans mb-2">
            {t('vocabularyLabel')}
          </h3>
          <dl className="space-y-2 text-sm">
            {day.vocabulary.map((v, i) => (
              <div key={i}>
                <dt className="text-[#f3d9a4] font-sans">{tx(v.term)}</dt>
                <dd className="text-amber-100/80 font-serif">— {tx(v.meaning)}</dd>
              </div>
            ))}
          </dl>
        </Card>
      )}

      {/* Mode tabs */}
      <div className="mt-5 grid grid-cols-4 gap-2">
        <TabButton
          active={tab === 'online'}
          onClick={() => setTab('online')}
          icon={Play}
          label={t('lessonOnlineMode')}
          testid="tab-online"
        />
        <TabButton
          active={tab === 'notebook'}
          onClick={() => setTab('notebook')}
          icon={NotebookPen}
          label={t('lessonNotebookMode')}
          testid="tab-notebook"
        />
        <Link to={`${base}/${weekId}/${dayId}/print`} data-testid="tab-print">
          <TabContent active={false} icon={Printer} label={t('lessonPrintMode')} />
        </Link>
        {/* Parent tab is parent-only. Tapping it as a child reveals a soft
            block message instead of opening the answer key. */}
        <TabButton
          active={tab === 'parent'}
          onClick={() => setTab('parent')}
          icon={Users}
          label={t('lessonParentMode')}
          testid="tab-parent"
        />
      </div>

      {/* Tab content */}
      {tab === 'online' && (
        <div className="mt-5 space-y-5" data-testid="lesson-online-panel">
          {(() => {
            const blocks = groupTasksByBlock(day.online);
            const totalSteps = blocks.length;
            const hasMultipleBlocks = totalSteps > 1;
            return blocks.map((block, blockIdx) => (
              <TaskBlock
                key={block.blockId}
                block={block}
                blockIdx={blockIdx}
                totalSteps={totalSteps}
                showHeader={hasMultipleBlocks}
                language={language}
                t={t}
                tx={tx}
                record={record}
                isParent={isParent}
                handleAnswer={handleAnswer}
              />
            ));
          })()}
          <SaveBadge active={savedFlash} t={t} />
        </div>
      )}
      {tab === 'notebook' && (
        <div className="mt-5" data-testid="lesson-notebook-panel">
          <Card className="p-5">
            <div
              className="text-[#f3d9a4] text-lg"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              {tx(day.notebook.title)}
            </div>
            <ol className="mt-3 space-y-2 list-decimal list-inside text-amber-100/85 font-serif text-sm leading-relaxed">
              {notebookSteps.map((s, i) => (
                <li key={i}>{s}</li>
              ))}
            </ol>
            <label className="mt-5 flex items-center gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={!!record.notebookDone}
                onChange={(e) => setNotebookDone(childId, weekId, dayId, e.target.checked)}
                disabled={isParent}
                className="accent-amber-400 w-5 h-5"
                data-testid="notebook-done-toggle"
              />
              <span className="text-[#f5ebe0]/85 font-sans">
                {t('notebookDoneToggle')}
              </span>
            </label>
          </Card>
        </div>
      )}

      {tab === 'parent' && (
        <div className="mt-5 space-y-3" data-testid="lesson-parent-panel">
          {isParent ? (
            <Card className="p-5">
              <Section title={t('parentGuideGoal')} body={tx(day.parentGuide.goal)} />
              <Section title={t('parentGuideCheck')} body={tx(day.parentGuide.check)} />
              <AnswerKeySection t={t} tx={tx} day={day} language={language} />
              <div>
                <h3 className="text-amber-200/75 font-sans text-sm uppercase tracking-wider mb-2">
                  {t('parentGuidePrompts')}
                </h3>
                <ul className="space-y-1.5 list-disc list-inside text-amber-100/85 font-serif text-sm">
                  {parentPrompts.map((p, i) => (
                    <li key={i}>{p}</li>
                  ))}
                </ul>
              </div>
              {(day.parentGuide.ifEasy || day.parentGuide.ifDifficult) && (
                <div className="mt-5 pt-4 border-t border-amber-300/10 grid sm:grid-cols-2 gap-3">
                  {day.parentGuide.ifEasy && (
                    <div
                      className="rounded-xl border border-emerald-300/20 bg-emerald-400/5 p-3"
                      data-testid="parent-if-easy"
                    >
                      <div className="text-emerald-200 font-sans text-xs uppercase tracking-wider mb-1.5">
                        {t('ifEasyLabel')}
                      </div>
                      <p className="text-amber-100/85 font-serif text-sm leading-relaxed">
                        {tx(day.parentGuide.ifEasy)}
                      </p>
                    </div>
                  )}
                  {day.parentGuide.ifDifficult && (
                    <div
                      className="rounded-xl border border-amber-300/20 bg-amber-300/5 p-3"
                      data-testid="parent-if-difficult"
                    >
                      <div className="text-amber-200 font-sans text-xs uppercase tracking-wider mb-1.5">
                        {t('ifDifficultLabel')}
                      </div>
                      <p className="text-amber-100/85 font-serif text-sm leading-relaxed">
                        {tx(day.parentGuide.ifDifficult)}
                      </p>
                    </div>
                  )}
                </div>
              )}
              {/* Phase 0 additive: optional extended teaching guidance.
                  Each field is rendered only if the lesson supplies it.
                  Max lessons that omit these fields render exactly as before. */}
              <ParentGuideOptional pg={day.parentGuide} tx={tx} language={language} />
              {/* Week-2 safety: structured parent-only theory card.
                  Never rendered for child users (the entire `tab === 'parent'`
                  block above is gated by `isParent`). Also never rendered in
                  PrintWorksheet — that page only uses `day.print.tasks`. */}
              <ParentTheoryCardSection
                ptc={day.parentGuide?.parentTheoryCard}
                language={language}
              />
              {/* Parent-only bonus reward controls */}
              {day.reward && Array.isArray(day.reward.bonuses) && day.reward.bonuses.length > 0 && (
                <ParentBonusControls
                  t={t}
                  day={day}
                  childId={childId}
                  weekId={weekId}
                  dayId={dayId}
                  hasBonusNimAward={hasBonusNimAward}
                  awardBonusNimForLesson={awardBonusNimForLesson}
                  getLessonNimAward={getLessonNimAward}
                />
              )}
            </Card>
          ) : (
            // Child view: NEVER render goal/check/answers/prompts. Show a
            // friendly "ask a parent" block with a CTA to the parent gate.
            <Card className="p-6 text-center" data-testid="lesson-parent-blocked">
              <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-amber-300/10 border border-amber-300/25 flex items-center justify-center text-amber-200/85">
                <Users className="w-6 h-6" />
              </div>
              <h3
                className="text-xl text-[#f3d9a4]"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                {t('parentRoomChildBlockTitle')}
              </h3>
              <p className="mt-2 text-amber-200/70 font-serif text-sm">
                {t('parentRoomChildBlockHint')}
              </p>
              <Link
                to="/lab/parent"
                className="inline-block mt-5 px-5 py-3 rounded-2xl bg-[#d4a574] hover:bg-[#c89860] text-[#1a1208] font-sans"
                data-testid="lesson-call-parent"
              >
                {t('callParent')}
              </Link>
            </Card>
          )}
        </div>
      )}

      {/* Reward preview — visible to everyone (child & parent) */}
      {day.reward && (
        <RewardPreview
          t={t}
          day={day}
          childId={childId}
          weekId={weekId}
          dayId={dayId}
          hasBaseNimAward={hasBaseNimAward}
          getNimBalance={getNimBalance}
        />
      )}

      {/* Complete action — PARENT ONLY. The child must never be able to mark
          a day done (and award nims) without doing the tasks. The parent
          controls completion from this page; child sees a neutral state. */}
      <div className="mt-6">
        {status === 'completed' ? (
          <div className="space-y-3">
            <div
              className="w-full py-4 rounded-2xl bg-emerald-400/10 border border-emerald-300/30 text-emerald-200 text-center font-sans flex items-center justify-center gap-2"
              data-testid="lesson-already-complete"
            >
              <CheckCircle2 className="w-5 h-5" />
              {t('lessonAlreadyComplete')}
            </div>
            {isParent && (
              <button
                type="button"
                onClick={handleReset}
                disabled={resetBusy}
                className="w-full py-3 rounded-2xl border border-amber-300/30 bg-[#0a0f26]/60 text-amber-200 hover:bg-amber-300/10 disabled:opacity-50 disabled:cursor-not-allowed font-sans text-sm flex items-center justify-center gap-2 transition-colors"
                data-testid="lesson-reset-button"
              >
                <RotateCcw className="w-4 h-4" />
                {resetBusy
                  ? (language === 'ru' ? 'Сбрасываю…' : 'Resetting…')
                  : (language === 'ru' ? 'Сбросить день' : 'Reset day')}
              </button>
            )}
            {(day.nextLessonId === 'max-week-10-night-run' ||
              day.nextLessonId === 'anita-week-10-night-run' ||
              (typeof day.nextLessonId === 'string' && day.nextLessonId.endsWith('week-10-night-run'))) && (
              <Link
                to={`${base}/nimny-night-run`}
                className="block w-full py-4 rounded-2xl bg-[#d4a574] hover:bg-[#c89860] text-[#1a1208] text-center font-sans transition-colors shadow-[0_0_24px_rgba(212,165,116,0.35)]"
                data-testid="lesson-open-night-game"
              >
                {t('questOpenNightGame')}
              </Link>
            )}
          </div>
        ) : isParent ? (
          <button
            type="button"
            onClick={handleComplete}
            disabled={completeBusy}
            className="w-full py-4 rounded-2xl bg-[#d4a574] hover:bg-[#c89860] disabled:opacity-60 disabled:cursor-not-allowed text-[#1a1208] font-sans transition-colors"
            data-testid="lesson-complete-button"
          >
            {completeBusy
              ? (language === 'ru' ? 'Завершаю…' : 'Completing…')
              : t('lessonComplete')}
          </button>
        ) : (
          <div
            className="w-full py-3 rounded-2xl border border-amber-300/25 bg-[#0a0f26]/60 text-amber-200/65 text-center font-sans text-sm"
            data-testid="lesson-child-await-parent"
          >
            {language === 'ru'
              ? 'Когда закончишь все задачи — попроси родителя завершить день в его режиме.'
              : 'When you finish all tasks, ask a parent to complete the day in their view.'}
          </div>
        )}
      </div>
        </div>
        {/* ----- Sidebar (desktop right column) ----- */}
        <aside className="lg:col-span-4 mt-4 lg:mt-0 space-y-3" data-testid="lesson-sidebar">
          <TodaysRouteCard
            t={t}
            language={language}
            courseTemplate={courseTemplate}
            blocks={groupTasksByBlock(day.online)}
            activeTab={tab}
            onJumpOnline={() => setTab('online')}
          />
          {Array.isArray(day.vocabulary) && day.vocabulary.length > 0 && (
            <Card className="p-5 hidden lg:block" data-testid="lesson-vocabulary-sidebar">
              <h3 className="text-amber-200/75 text-xs uppercase tracking-wider font-sans mb-2">
                {t('vocabularyLabel')}
              </h3>
              <dl className="space-y-2 text-sm">
                {day.vocabulary.map((v, i) => (
                  <div key={i}>
                    <dt className="text-[#f3d9a4] font-sans">{tx(v.term)}</dt>
                    <dd className="text-amber-100/80 font-serif">— {tx(v.meaning)}</dd>
                  </div>
                ))}
              </dl>
            </Card>
          )}
          <SidebarActionsCard
            t={t}
            language={language}
            base={base}
            weekId={weekId}
            dayId={dayId}
            tab={tab}
            setTab={setTab}
            isParent={isParent}
          />
        </aside>
      </div>
    </LabLayout>
  );
};

const TabButton = ({ active, onClick, icon: Icon, label, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}>
    <TabContent active={active} icon={Icon} label={label} />
  </button>
);

const TabContent = ({ active, icon: Icon, label }) => (
  <div
    className={`aspect-square rounded-2xl border flex flex-col items-center justify-center gap-1.5 p-2 transition-colors ${
      active
        ? 'border-amber-300/45 bg-amber-300/10'
        : 'border-amber-300/15 bg-[#0e1530]/70 hover:bg-amber-300/5'
    }`}
  >
    <Icon
      className={`w-6 h-6 ${
        active ? 'text-amber-300' : 'text-amber-300/85'
      } drop-shadow-[0_0_8px_rgba(212,165,116,0.35)]`}
    />
    <span className="text-[#f5ebe0]/85 font-sans text-[10px] text-center leading-tight">
      {label}
    </span>
  </div>
);

const Section = ({ title, body }) => (
  <div className="mb-4 last:mb-0">
    <h3 className="text-amber-200/75 font-sans text-sm uppercase tracking-wider mb-2">
      {title}
    </h3>
    <p className="text-amber-100/85 font-serif text-sm leading-relaxed">{body}</p>
  </div>
);

// ----------------------------------------------------------------------------
// Parent-only Answer Key renderer.
// Splits `parentGuide.answerKey` (preferred, array of { taskId, answer })
// into per-task cards with structured Answer / Solution / Parent note blocks.
// Falls back to parsing a legacy concatenated `parentGuide.answers` string.
// Renders nothing if neither field is present.
// SAFETY: this component is mounted only inside the parent tab block, which
// is gated by `isParent` higher in Lesson.jsx. Child view never reaches it.
// ----------------------------------------------------------------------------

// Pick the side of "RU / EN" bilingual answer text matching the current lang.
const pickAnswerLang = (raw, language) => {
  if (typeof raw !== 'string') return '';
  const text = raw.trim();
  if (!text) return '';
  // Only split on " / " when both sides look plausible (>= 2 chars each)
  // to avoid breaking math like "1 / 4" or "240 ÷ 10 = 24".
  const m = text.split(/\s\/\s+/);
  if (m.length < 2) return text;
  // Heuristic: first half = RU, last half = EN. Re-join intermediate halves
  // back to the RU side so equations like "1 / 4" survive.
  const last = m[m.length - 1];
  const looksEn = /[A-Za-z]/.test(last) && !/[А-Яа-яЁё]/.test(last);
  if (!looksEn) return text;
  const ru = m.slice(0, -1).join(' / ').trim();
  const en = last.trim();
  return language === 'en' ? (en || ru) : (ru || en);
};

// Detect label boundaries inside a single-language answer string.
// Supported labels (both EN and RU):
//   Answer: / Ответ:
//   Solution: / Решение: / Шаг: / Шаги:
//   Parent note: / Подсказка родителю:
const ANSWER_LABEL_REGEX =
  /(^|\n|\.\s|;\s)(Answer:|Ответ:|Solution:|Решение:|Шаги?:|Parent note:|Подсказка родителю:)/g;

const LABEL_KIND = {
  'Answer:': 'answer',
  'Ответ:': 'answer',
  'Solution:': 'solution',
  'Решение:': 'solution',
  'Шаг:': 'solution',
  'Шаги:': 'solution',
  'Parent note:': 'note',
  'Подсказка родителю:': 'note',
};

const LABEL_TITLES = {
  ru: { answer: 'Ответ', solution: 'Решение', note: 'Подсказка родителю' },
  en: { answer: 'Answer', solution: 'Solution', note: 'Parent note' },
};

const splitLabeledBlocks = (text) => {
  if (!text) return [];
  const blocks = [];
  const matches = [];
  let m;
  ANSWER_LABEL_REGEX.lastIndex = 0;
  while ((m = ANSWER_LABEL_REGEX.exec(text)) !== null) {
    matches.push({
      start: m.index + (m[1] ? m[1].length : 0),
      labelEnd: m.index + m[0].length,
      label: m[2],
    });
  }
  if (matches.length === 0) {
    return [{ kind: 'plain', content: text.trim() }];
  }
  // Anything before the first label = headline content (often the bare answer).
  const headline = text.slice(0, matches[0].start).trim();
  if (headline) blocks.push({ kind: 'answer', content: headline });
  for (let i = 0; i < matches.length; i += 1) {
    const cur = matches[i];
    const next = matches[i + 1];
    const end = next ? next.start : text.length;
    const content = text.slice(cur.labelEnd, end).trim().replace(/^\s*[—:]\s*/, '');
    if (content) blocks.push({ kind: LABEL_KIND[cur.label] || 'note', content });
  }
  // De-duplicate: if we already extracted a headline as 'answer' and the first
  // labeled block is also 'answer', merge them.
  if (blocks.length >= 2 && blocks[0].kind === 'answer' && blocks[1].kind === 'answer') {
    blocks[0] = { kind: 'answer', content: `${blocks[0].content}\n${blocks[1].content}` };
    blocks.splice(1, 1);
  }
  return blocks;
};

// Friendly task label e.g. "q_w1" → "Разминка 1".
const prettyTaskBadge = (taskId) => {
  if (!taskId) return '?';
  return taskId
    .replace(/^q_?w(\d+)$/i, 'W$1')
    .replace(/^q_?s(\d+)$/i, 'S$1')
    .replace(/^q_corr$/i, 'CORR')
    .replace(/^q_obs$/i, 'OBS')
    .replace(/^q(\d+)$/i, 'Q$1')
    .toUpperCase();
};

const BLOCK_STYLES = {
  answer: {
    box: 'border-emerald-300/25 bg-emerald-400/[0.05]',
    label: 'text-emerald-200/85',
  },
  solution: {
    box: 'border-amber-300/20 bg-amber-300/[0.04]',
    label: 'text-amber-200/85',
  },
  note: {
    box: 'border-sky-300/20 bg-sky-400/[0.05]',
    label: 'text-sky-200/85',
  },
  plain: {
    box: 'border-amber-300/15 bg-amber-300/[0.03]',
    label: 'text-amber-200/70',
  },
};

const AnswerBlock = ({ block, language }) => {
  if (!block || !block.content) return null;
  const style = BLOCK_STYLES[block.kind] || BLOCK_STYLES.plain;
  const title = block.kind === 'plain' ? null : LABEL_TITLES[language === 'en' ? 'en' : 'ru'][block.kind];
  return (
    <div className={`rounded-lg border ${style.box} px-3 py-2`}>
      {title && (
        <div className={`text-[10px] font-sans uppercase tracking-wider mb-1 ${style.label}`}>
          {title}
        </div>
      )}
      <div className="text-amber-100/90 font-serif text-[13px] leading-relaxed whitespace-pre-wrap">
        {block.content}
      </div>
    </div>
  );
};

const AnswerKeyCard = ({ entry, taskTitle, language }) => {
  const ruEn = pickAnswerLang(entry.answer, language);
  const blocks = splitLabeledBlocks(ruEn);
  return (
    <div
      className="rounded-xl border border-amber-300/15 bg-[#1c1208]/40 px-3.5 py-3"
      data-testid={`parent-answer-card-${entry.taskId || 'unknown'}`}
    >
      <div className="flex items-baseline gap-2 mb-2 flex-wrap">
        <span className="inline-flex items-center justify-center rounded-md bg-amber-300/15 border border-amber-300/25 text-amber-200/90 text-[11px] font-sans font-medium tracking-wider px-1.5 py-0.5 shrink-0">
          {prettyTaskBadge(entry.taskId)}
        </span>
        {taskTitle && (
          <span className="text-amber-100/80 font-sans text-xs">
            {taskTitle}
          </span>
        )}
      </div>
      <div className="space-y-2">
        {blocks.map((b, i) => (
          <AnswerBlock key={i} block={b} language={language} />
        ))}
      </div>
    </div>
  );
};

// Parse a legacy concatenated "answers" string into pseudo-entries by
// splitting at task-id markers like `q1:` / `q_w1:` / `q_corr:` / `q_obs:`.
const parseLegacyAnswersString = (raw) => {
  if (typeof raw !== 'string') return null;
  const text = raw.trim();
  if (!text) return null;
  // Look for ` qXX: ` / ` q_xxx: ` markers (whitespace or start as boundary).
  const re = /(^|\s)(q_?[a-z]?\d+|q_corr|q_obs|q_s\d+|q_w\d+):\s/gi;
  const hits = [];
  let m;
  while ((m = re.exec(text)) !== null) {
    hits.push({ idStart: m.index + (m[1] ? m[1].length : 0), idEnd: re.lastIndex, taskId: m[2] });
  }
  if (hits.length === 0) return null;
  const entries = [];
  for (let i = 0; i < hits.length; i += 1) {
    const cur = hits[i];
    const next = hits[i + 1];
    const end = next ? next.idStart : text.length;
    const body = text.slice(cur.idEnd, end).trim();
    if (body) entries.push({ taskId: cur.taskId.toLowerCase(), answer: body });
  }
  return entries;
};

const AnswerKeySection = ({ t, tx, day, language }) => {
  if (!day || !day.parentGuide) return null;
  const pg = day.parentGuide;
  let entries = Array.isArray(pg.answerKey) ? pg.answerKey.filter((e) => e && e.answer) : null;
  let legacyText = null;
  if (!entries || entries.length === 0) {
    const rawAnswers = tx(pg.answers);
    if (typeof rawAnswers === 'string' && rawAnswers.trim()) {
      const parsed = parseLegacyAnswersString(rawAnswers);
      if (parsed && parsed.length) {
        entries = parsed;
      } else {
        legacyText = rawAnswers;
      }
    }
  }
  if ((!entries || entries.length === 0) && !legacyText) return null;

  // Build a taskId → title lookup from onlineTasks for nicer card headers.
  const titleByTaskId = {};
  for (const ot of (day.onlineTasks || [])) {
    if (ot && ot.id) titleByTaskId[ot.id] = tx(ot.title) || ot.title;
  }

  return (
    <div className="mb-4" data-testid="parent-answer-key">
      <h3 className="text-amber-200/75 font-sans text-sm uppercase tracking-wider mb-2">
        {t('parentGuideAnswers')}
      </h3>
      {entries && entries.length > 0 ? (
        <div className="space-y-2.5">
          {entries.map((entry, i) => (
            <AnswerKeyCard
              key={`${entry.taskId || 'task'}-${i}`}
              entry={entry}
              taskTitle={titleByTaskId[entry.taskId]}
              language={language}
            />
          ))}
        </div>
      ) : (
        <pre className="text-amber-100/85 font-serif text-[13px] leading-relaxed whitespace-pre-wrap bg-[#1c1208]/40 border border-amber-300/15 rounded-xl px-3.5 py-3">
          {legacyText}
        </pre>
      )}
    </div>
  );
};

// Reward preview card — visible above the Complete button.
// For child: shows base + bonus cap; once awarded, shows current balance.
// For parent: same, plus a clear hint that bonuses live in the Parent Room.
const RewardPreview = ({ t, day, childId, weekId, dayId, hasBaseNimAward, getNimBalance }) => {
  const reward = day.reward;
  const lessonKey = `${childId}-${weekId}-${dayId}`;
  const baseAwarded = hasBaseNimAward(childId, lessonKey);
  const balance = getNimBalance(childId);
  const isProject = (reward.label || '').includes('проект');
  const bonusMax = (reward.bonuses || []).reduce(
    (acc, b) => acc + (Number(b.amount) || 0),
    0
  );
  return (
    <Card className="mt-5 p-4" data-testid="lesson-reward-preview">
      <div className="flex items-start gap-3">
        <span className="w-10 h-10 rounded-full bg-amber-300/8 border border-amber-300/15 flex items-center justify-center text-amber-200/85 shrink-0">
          <Coins className="w-5 h-5" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-amber-200/75 font-sans text-xs uppercase tracking-wider">
            {t('rewardOfTheDay')}
          </div>
          <div className="text-[#f5ebe0]/90 font-serif text-sm mt-1">
            {isProject ? t('rewardForProject') : t('rewardForCompletion')}
            {': '}
            <span className="text-[#f3d9a4]">{formatNims(reward.base)}</span>
          </div>
          {bonusMax > 0 && (
            <div className="text-amber-200/75 font-serif text-sm">
              {t('rewardBonusUpTo')}{' '}
              <span className="text-[#f3d9a4]">{formatNims(bonusMax)}</span>
            </div>
          )}
          {baseAwarded && (
            <div
              className="mt-2 text-emerald-200/85 font-sans text-xs"
              data-testid="lesson-reward-already-awarded"
            >
              {t('rewardAlreadyAwarded')} {t('kopilkaBalanceLabel')}{' '}
              {formatNims(balance)}.
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

// Parent-only bonus controls (rendered inside the parent guide card,
// so they only become part of the DOM when role === 'parent'; child
// view never renders this branch).
const ParentBonusControls = ({
  t,
  day,
  childId,
  weekId,
  dayId,
  hasBonusNimAward,
  awardBonusNimForLesson,
  getLessonNimAward
}) => {
  const reward = day.reward;
  const lessonKey = `${childId}-${weekId}-${dayId}`;
  const award = getLessonNimAward(childId, lessonKey);
  const currentTotal = Number(award?.total || 0);
  const max = Number(reward.max || 0);
  const handleAward = (bonus) => {
    awardBonusNimForLesson(childId, {
      id: lessonKey,
      weekId,
      dayId,
      title: day.title,
      reward
    }, bonus.id);
  };
  return (
    <div className="mt-5 pt-4 border-t border-amber-300/10" data-testid="parent-bonus-controls">
      <h3 className="text-amber-200/75 font-sans text-sm uppercase tracking-wider mb-2">
        {t('parentBonusTitle')}
      </h3>
      <p className="text-amber-200/65 font-serif text-xs mb-3">
        {t('parentBonusHint')}
      </p>
      <div className="space-y-2">
        {reward.bonuses.map((bonus) => {
          const granted = hasBonusNimAward(childId, lessonKey, bonus.id);
          const wouldExceedCap = !granted && currentTotal + bonus.amount > max;
          const disabled = granted || wouldExceedCap;
          return (
            <button
              key={bonus.id}
              type="button"
              onClick={() => handleAward(bonus)}
              disabled={disabled}
              data-testid={`parent-bonus-${bonus.id}`}
              className={`w-full text-left flex items-center justify-between gap-3 px-4 py-3 rounded-xl border font-sans transition-colors ${
                granted
                  ? 'border-emerald-300/30 bg-emerald-400/5 text-emerald-200/85 cursor-default'
                  : 'border-amber-300/20 bg-[#0e1530]/70 text-[#f5ebe0]/90 hover:bg-amber-300/5'
              } ${disabled ? 'opacity-70' : ''}`}
            >
              <span className="flex-1 text-sm">
                {bonus.label}{' '}
                <span className="text-amber-200/60">
                  (+{formatNims(bonus.amount)})
                </span>
              </span>
              <span className="shrink-0 text-xs">
                {granted ? t('parentBonusAlready') : t('parentBonusAdd')}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const SaveBadge = ({ active, t }) => (
  <div
    className={`flex items-center justify-center gap-1.5 text-xs font-sans transition-opacity ${
      active ? 'opacity-100 text-emerald-300' : 'opacity-40 text-amber-200/60'
    }`}
  >
    <Save className="w-3.5 h-3.5" />
    {t('saved')}
  </div>
);

// ---------- Optional English helper rendering for onlineTasks (Phase 0) ----------
// Renders an optional secondary English helper line BELOW the Russian prompt.
// - `enHelpText` (preferred) or `englishHelp` (alias) → visually muted helper.
// - Russian primary text remains the dominant content; this never replaces it.
// Max lessons that don't supply these fields render exactly as before.
const TaskEnglishHelper = ({ q, tx, derivedEnHelp }) => {
  // Render the secondary English helper.
  // Priority:
  //   1) `derivedEnHelp` — the English half auto-split from a bilingual
  //      "RU text / EN text" prompt (passed down by TaskCard). This is
  //      how legacy Max lessons surface their English help without any
  //      data-file changes.
  //   2) `enHelpText` — the canonical Anita schema field.
  //   3) `englishHelp` — an older alias kept for compatibility.
  // `q.helper` is intentionally excluded: in Max math lessons it is a
  // numeric hint already routed to `q.hint` by `normTask`. Treating it
  // as English help would surface a math expression under the wrong label.
  let resolved = (derivedEnHelp || '').trim();
  if (!resolved) {
    const candidate = q?.enHelpText ?? q?.englishHelp;
    resolved = (resolveBilingual(candidate, tx) || '').trim();
  }
  if (!resolved) return null;
  return (
    <details
      className="mt-2 group"
      data-testid={`q-${q.id}-en-helper`}
    >
      <summary
        className="cursor-pointer select-none text-amber-200/55 hover:text-amber-200/75 text-[11px] uppercase tracking-wider font-sans inline-flex items-center gap-1"
        data-testid={`q-${q.id}-en-helper-toggle`}
      >
        English help
        <span className="text-amber-200/35 group-open:rotate-90 transition-transform">›</span>
      </summary>
      <p className="mt-1.5 text-amber-200/55 font-serif text-xs leading-relaxed italic whitespace-pre-line">
        {resolved}
      </p>
    </details>
  );
};

// ---------- Mission-block grouping (UI-only) --------------------------------
// Groups onlineTasks into pedagogical blocks ("reading", "math", "spelling",
// "creative", "speech", "practical", "mission"). Lesson DATA is not touched;
// this only changes how the child sees the task list (block headers + small
// "Step X of Y" counters instead of a long 1/N list).
const BLOCK_DEFS = {
  reading:   { order: 1, color: 'amber',    ru: 'Чтение',            en: 'Reading',           emoji: '📖' },
  math:      { order: 2, color: 'amber',    ru: 'Математика',         en: 'Math',              emoji: '🧮' },
  spelling:  { order: 3, color: 'amber',    ru: 'Правописание',       en: 'Spelling',          emoji: '✍️' },
  practical: { order: 4, color: 'amber',    ru: 'Ситуация и миссия',  en: 'Situation & Mission', emoji: '🧭' },
  creative:  { order: 5, color: 'amber',    ru: 'Бумага и рисунок',   en: 'Paper & Drawing',   emoji: '🎨' },
  speech:    { order: 6, color: 'amber',    ru: 'Объясни родителю',   en: 'Explain to a Parent', emoji: '🗣' },
  mission:   { order: 99, color: 'amber',   ru: 'Миссия',            en: 'Mission',           emoji: '🏷' }
};

const inferBlockId = (q) => {
  if (q?.blockId && BLOCK_DEFS[q.blockId]) return q.blockId;
  const id = String(q?.id || '').toLowerCase();
  const type = String(q?.type || '').toLowerCase();
  const titleRaw = q?.title;
  const title = typeof titleRaw === 'string'
    ? titleRaw.toLowerCase()
    : ((titleRaw?.ru || titleRaw?.en || '') + '').toLowerCase();

  if (id.startsWith('reading-') || /\breading\b/.test(id) || /чтени|reading/.test(title)) return 'reading';
  if (id.startsWith('math-') || type === 'math' || /матема|math/.test(title)) return 'math';
  if (id.startsWith('spelling-') || /spelling|правописан|орфограф/.test(title)) return 'spelling';
  if (id.startsWith('creative-') || id.startsWith('project-') || /проект|идея|creative|drawing|рисун|карточк/.test(title)) return 'creative';
  if (id.startsWith('speech-') || id.startsWith('oral-') || /speech|oral|объясни|расскажи|скажи/.test(title)) return 'speech';
  if (id.startsWith('safety-') || id.startsWith('practical-') || type === 'classify' || /safety|practical|ситуац|порядок|маршрут/.test(title)) return 'practical';
  return 'mission';
};

// Returns: [{ blockId, def, tasks }] in first-appearance order so the on-screen
// flow mirrors the author's intent within each lesson.
const groupTasksByBlock = (tasks) => {
  if (!Array.isArray(tasks) || tasks.length === 0) return [];
  const order = [];
  const map = new Map();
  for (const q of tasks) {
    const bid = inferBlockId(q);
    if (!map.has(bid)) {
      order.push(bid);
      map.set(bid, []);
    }
    map.get(bid).push(q);
  }
  return order.map((bid) => ({
    blockId: bid,
    def: BLOCK_DEFS[bid] || BLOCK_DEFS.mission,
    tasks: map.get(bid)
  }));
};

// Per-course "Today's Route" labels (sidebar). Each entry maps to one or
// more block ids so we can mark which steps the current lesson covers.
const ROUTE_BY_COURSE = {
  anita: [
    { id: 'reading-words',     ru: 'Слова',                en: 'Words',                 blocks: ['reading'] },
    { id: 'reading',           ru: 'Чтение',                en: 'Reading',               blocks: ['reading'] },
    { id: 'math',              ru: 'Математика',            en: 'Math',                  blocks: ['math'] },
    { id: 'situation',         ru: 'Ситуация / миссия',     en: 'Situation / Mission',   blocks: ['practical', 'mission'] },
    { id: 'paper',             ru: 'Бумага / рисунок',      en: 'Paper / Drawing',       blocks: ['creative', 'spelling'] },
    { id: 'explain',           ru: 'Объясни родителю',      en: 'Explain to a Parent',   blocks: ['speech'] }
  ],
  max: [
    { id: 'math-lab',          ru: 'Математическая лаборатория', en: 'Math Lab',            blocks: ['math'] },
    { id: 'russian',           ru: 'Русский',                en: 'Russian',                blocks: ['spelling', 'reading'] },
    { id: 'reading-writing',   ru: 'Чтение и письмо',         en: 'Reading / Writing',      blocks: ['reading', 'creative'] },
    { id: 'mission',           ru: 'Миссия из жизни',         en: 'Real World Mission',     blocks: ['practical', 'mission'] },
    { id: 'diary',             ru: 'Дневник мышления',        en: 'Thinking Diary',         blocks: ['speech'] },
    { id: 'final-check',       ru: 'Финальная проверка',      en: 'Final Check',            blocks: ['mission'] }
  ]
};

const buildTodaysRoute = (courseTemplate, blocks) => {
  const list = ROUTE_BY_COURSE[courseTemplate] || ROUTE_BY_COURSE.max;
  const present = new Set(blocks.map((b) => b.blockId));
  return list.map((step) => ({
    ...step,
    active: step.blocks.some((bid) => present.has(bid))
  }));
};

// ---------- Mission block + task card components ----------------------------
// Each TaskBlock groups related onlineTasks under a single header so the
// child sees pedagogical missions instead of a raw 1/N list. Inside each
// block the small counter is "Шаг X из Y / Step X of Y".
const TaskBlock = ({ block, blockIdx, totalSteps, showHeader, language, t, tx, record, isParent, handleAnswer }) => {
  const def = block.def;
  const blockTitle = language === 'ru' ? def.ru : def.en;
  const blockSub = language === 'ru' ? def.en : def.ru;
  const innerCount = block.tasks.length;
  return (
    <section
      className="rounded-2xl border border-amber-300/15 bg-[#0a0f26]/45 p-3 sm:p-4"
      data-testid={`lesson-block-${block.blockId}`}
    >
      {showHeader && (
        <header className="px-1 pb-3 border-b border-amber-300/10 mb-3 flex items-center gap-3">
          <span className="text-2xl leading-none" aria-hidden="true">{def.emoji}</span>
          <div className="flex-1 min-w-0">
            <div
              className="text-[#f3d9a4] text-base sm:text-lg leading-tight"
              style={{ fontFamily: 'var(--font-heading)' }}
              data-testid={`lesson-block-${block.blockId}-title`}
            >
              {blockTitle}
            </div>
            <div className="text-amber-200/50 font-sans text-[11px] uppercase tracking-wider">
              {blockSub}
            </div>
          </div>
          <span className="shrink-0 text-amber-200/65 font-sans text-xs whitespace-nowrap">
            {language === 'ru'
              ? `Шаг ${blockIdx + 1} из ${totalSteps}`
              : `Step ${blockIdx + 1} of ${totalSteps}`}
          </span>
        </header>
      )}
      <div className="space-y-3">
        {block.tasks.map((q, i) => (
          <TaskCard
            key={q.id}
            q={q}
            stepIdx={i}
            totalInBlock={innerCount}
            language={language}
            t={t}
            tx={tx}
            record={record}
            isParent={isParent}
            handleAnswer={handleAnswer}
          />
        ))}
      </div>
    </section>
  );
};

// Bilingual-or-plain string resolver.
// Lesson data files store some fields as plain Russian strings
// (`ruPrimaryText`, `title`, `enHelpText`, ...). The `index.js` adapter
// wraps a handful of fields (`prompt`, `hint`, option/category labels,
// `unit`) into `{ru, en}` shape — everything else passes through as a
// plain string. `tx()` from LabContext only resolves the `{ru, en}` shape
// and returns `''` for plain strings, which made the primary Russian
// prompt invisible inside task cards. This helper handles both shapes
// so the child always sees the Russian instruction.
const resolveBilingual = (value, tx) => {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  return tx(value) || '';
};

// First non-empty value from a list of candidate fields on a task.
const firstNonEmpty = (q, keys, tx) => {
  for (const k of keys) {
    const resolved = resolveBilingual(q?.[k], tx);
    if (resolved && resolved.trim().length > 0) return resolved;
  }
  return '';
};

// Split a bilingual "RU text / EN text" string at the FIRST " / " marker.
// Many Max-week lesson prompts and hints embed Russian and English inline,
// separated by " / " (with surrounding spaces). For these tasks we want the
// child to see the Russian half as primary content and the English half as
// the muted secondary helper — same UX as Anita lessons that store the two
// halves in separate fields. Anita prompts contain no " / " separator, so
// they are returned unchanged. Fraction notation like "1/2" (no spaces) is
// preserved because we look only for " / " with spaces on both sides.
const splitBilingualSlash = (s) => {
  if (typeof s !== 'string' || !s) return { ru: s || '', en: '' };
  // Find every ` / ` separator (with spaces — we never split on bare `/` so
  // arithmetic like `1/2` and dates like `2024/03` stay intact).
  const positions = [];
  let i = s.indexOf(' / ');
  while (i !== -1) {
    positions.push(i);
    i = s.indexOf(' / ', i + 1);
  }
  if (positions.length === 0) return { ru: s, en: '' };
  if (positions.length === 1) {
    return { ru: s.slice(0, positions[0]).trim(), en: s.slice(positions[0] + 3).trim() };
  }
  // Multiple ` / ` separators (e.g. "Что сделать / What to do: <RU> / <EN>").
  // Pick the split whose preceding non-space character ends a Russian
  // sentence: . ? ! ) » ” — that's the boundary between the full RU prompt
  // and its English mirror. Fallback to the first ` / ` so legacy lessons
  // with a single label-style split keep their original behaviour.
  const endsSentence = (idx) => {
    let j = idx - 1;
    while (j >= 0 && /\s/.test(s[j])) j -= 1;
    if (j < 0) return false;
    return /[.!?)»”]/.test(s[j]);
  };
  // Walk from the last separator backwards so prompts that end with a
  // sentence-final question or period give us the most "balanced" RU/EN cut.
  for (let k = positions.length - 1; k >= 0; k -= 1) {
    if (endsSentence(positions[k])) {
      return {
        ru: s.slice(0, positions[k]).trim(),
        en: s.slice(positions[k] + 3).trim(),
      };
    }
  }
  return { ru: s.slice(0, positions[0]).trim(), en: s.slice(positions[0] + 3).trim() };
};

const TaskCard = ({ q, stepIdx, totalInBlock, language, t, tx, record, isParent, handleAnswer }) => {
  const taskTitle = resolveBilingual(q.title, tx);
  // Primary Russian instruction — fallback chain.
  // We prefer `prompt` because it is the canonical FULL task content
  // (lists, sentences, cards, math problems). `ruPrimaryText` is often
  // a one-line summary written by the author; if it is the only field
  // present we still render it, but never let it hide the full prompt.
  const rawPrimary = firstNonEmpty(q, ['prompt', 'ruPrimaryText', 'question', 'text'], tx);
  // If the task supplies an explicit EN helper field, use the prompt as-is
  // (Anita schema). Otherwise auto-split the inline "RU / EN" prompt so
  // the English half drops into the collapsible English-help slot below.
  const explicitEnHelp = resolveBilingual(q.enHelpText ?? q.englishHelp, tx);
  let primaryText = rawPrimary;
  let derivedEnHelp = explicitEnHelp;
  if (!derivedEnHelp) {
    const split = splitBilingualSlash(rawPrimary);
    if (split.en) {
      primaryText = split.ru;
      derivedEnHelp = split.en;
    }
  }
  return (
    <Card className="p-4" data-testid={`task-card-${q.id}`}>
      <div className="mb-3">
        <div className="text-amber-200/55 text-[11px] uppercase tracking-wider font-sans">
          {language === 'ru'
            ? `Задание ${stepIdx + 1} из ${totalInBlock}`
            : `Task ${stepIdx + 1} of ${totalInBlock}`}
        </div>
        {taskTitle && (
          <h4
            className="mt-1 text-[#f3d9a4] font-sans text-sm sm:text-base leading-snug"
            data-testid={`task-${q.id}-title`}
          >
            {taskTitle}
          </h4>
        )}
      </div>
      {primaryText && (
        <p
          className="text-[#f5ebe0]/95 font-serif text-base leading-relaxed whitespace-pre-line"
          data-testid={`task-${q.id}-prompt`}
        >
          {primaryText}
        </p>
      )}
      <TaskEnglishHelper q={q} tx={tx} derivedEnHelp={derivedEnHelp} />
      <TaskInputs
        q={q}
        record={record}
        isParent={isParent}
        handleAnswer={handleAnswer}
        t={t}
        tx={tx}
      />
    </Card>
  );
};

// Renders an onlineTask hint line. Splits an inline "RU / EN" hint so the
// Russian half stays on the primary baseline and the English half (if any)
// appears as smaller muted italic — same RU-first hierarchy as the
// English-help block above. A hint that is Russian-only (Anita schema or
// Russian-only Max hints) renders unchanged on a single line.
const HintLine = ({ q, t, tx, layout = 'block' }) => {
  if (!q?.hint) return null;
  const raw = tx(q.hint) || '';
  if (!raw.trim()) return null;
  const { ru, en } = splitBilingualSlash(raw);
  const labelPrefix = layout === 'inlineMath' ? '' : `${t('hint')}: `;
  if (!en) {
    return (
      <p className={
        layout === 'inlineMath'
          ? 'ml-2 text-amber-200/55 text-xs font-serif italic flex-1'
          : 'mt-2 text-amber-200/55 text-xs font-serif italic'
      }>
        {labelPrefix}{ru}
      </p>
    );
  }
  return (
    <div className={
      layout === 'inlineMath'
        ? 'ml-2 text-xs font-serif flex-1 leading-snug'
        : 'mt-2 text-xs font-serif leading-snug'
    }>
      <p className="text-amber-200/70 italic">
        {labelPrefix}{ru}
      </p>
      <p className="mt-0.5 text-amber-200/45 italic">
        {en}
      </p>
    </div>
  );
};

// Renders all the input controls for one onlineTask. Kept verbatim from the
// previous flat list (text / choice / classify / math / essay) so behaviour
// and test ids are unchanged.
const TaskInputs = ({ q, record, isParent, handleAnswer, t, tx }) => (
  <>
    {q.type === 'text' && (
      <>
        <textarea
          rows={3}
          placeholder={t('answerPlaceholder')}
          className="mt-3 w-full p-3 rounded-xl bg-[#0a0f26]/80 border border-amber-300/20 focus:border-amber-300/40 focus:outline-none text-[#f5ebe0] placeholder:text-amber-200/35 font-serif resize-none"
          value={record.responses?.[q.id] || ''}
          onChange={(e) => handleAnswer(q.id, e.target.value)}
          readOnly={isParent}
          data-testid={`q-${q.id}-input`}
        />
        <HintLine q={q} t={t} tx={tx} />
      </>
    )}
    {q.type === 'choice' && (
      <div className="mt-3 grid grid-cols-1 gap-2">
        {q.options.map((opt) => {
          const selected = (record.responses?.[q.id] || []).includes(opt.id);
          return (
            <button
              key={opt.id}
              type="button"
              disabled={isParent}
              onClick={() => {
                const cur = record.responses?.[q.id] || [];
                const next = selected
                  ? cur.filter((x) => x !== opt.id)
                  : [...cur, opt.id];
                handleAnswer(q.id, next);
              }}
              className={`text-left px-4 py-3 rounded-xl border font-sans transition-colors ${
                selected
                  ? 'border-amber-300/45 bg-amber-300/10 text-[#f5ebe0]'
                  : 'border-amber-300/15 bg-[#0a0f26]/60 text-[#f5ebe0]/85 hover:bg-amber-300/5'
              }`}
              data-testid={`q-${q.id}-opt-${opt.id}`}
            >
              {tx(opt.label)}
            </button>
          );
        })}
      </div>
    )}
    {q.type === 'classify' && (
      <div className="mt-3 space-y-3" data-testid={`q-${q.id}-classify`}>
        {q.items.map((item) => {
          const assigned = record.responses?.[q.id]?.[item.id] || null;
          return (
            <div
              key={item.id}
              className="rounded-xl border border-amber-300/15 bg-[#0a0f26]/60 p-3"
              data-testid={`q-${q.id}-item-${item.id}`}
            >
              <div className="text-[#f5ebe0]/90 font-serif text-sm mb-2">
                {tx(item.label)}
              </div>
              <div className="grid grid-cols-2 gap-1.5">
                {q.categories.map((cat) => {
                  const isSel = assigned === cat.id;
                  return (
                    <button
                      key={cat.id}
                      type="button"
                      disabled={isParent}
                      onClick={() => {
                        const cur = record.responses?.[q.id] || {};
                        handleAnswer(q.id, { ...cur, [item.id]: cat.id });
                      }}
                      className={`px-2 py-2 rounded-lg text-xs font-sans transition-colors border ${
                        isSel
                          ? 'border-amber-300/45 bg-amber-300/15 text-[#f5ebe0]'
                          : 'border-amber-300/15 bg-[#0e1530]/70 text-amber-200/75 hover:bg-amber-300/5'
                      }`}
                      data-testid={`q-${q.id}-item-${item.id}-cat-${cat.id}`}
                    >
                      {tx(cat.label)}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
        {q.hint && (
          <HintLine q={q} t={t} tx={tx} />
        )}
      </div>
    )}
    {q.type === 'math' && (
      <div className="mt-3 flex items-center gap-2" data-testid={`q-${q.id}-math`}>
        <input
          type="text"
          inputMode="text"
          placeholder="?"
          value={record.responses?.[q.id] || ''}
          onChange={(e) => handleAnswer(q.id, e.target.value.replace(/[^0-9.,\-+/ %⅛¼⅓½⅜⅝⅔¾⅞]/g, ''))}
          readOnly={isParent}
          className="w-40 px-4 py-3 rounded-xl bg-[#0a0f26]/80 border border-amber-300/20 focus:border-amber-300/45 focus:outline-none text-[#f5ebe0] placeholder:text-amber-200/30 font-sans text-center text-lg"
          data-testid={`q-${q.id}-input`}
        />
        {q.unit && (
          <span className="text-amber-200/70 font-sans text-sm">{tx(q.unit)}</span>
        )}
        <HintLine q={q} t={t} tx={tx} layout="inlineMath" />
      </div>
    )}
    {q.type === 'essay' && (
      <>
        <textarea
          rows={6}
          placeholder={t('answerPlaceholder')}
          className="mt-3 w-full p-3 rounded-xl bg-[#0a0f26]/80 border border-amber-300/20 focus:border-amber-300/40 focus:outline-none text-[#f5ebe0] placeholder:text-amber-200/35 font-serif resize-none"
          value={record.responses?.[q.id] || ''}
          onChange={(e) => handleAnswer(q.id, e.target.value)}
          readOnly={isParent}
          data-testid={`q-${q.id}-input`}
        />
        <div className="mt-1.5 flex items-start justify-between text-xs font-sans gap-2">
          <div className="flex-1 min-w-0">
            <HintLine q={q} t={t} tx={tx} />
          </div>
          {q.minSentences && q.maxSentences && (
            <span className="text-amber-200/45 shrink-0">
              {q.minSentences}–{q.maxSentences} {t('sentencesLabel')}
            </span>
          )}
        </div>
      </>
    )}
  </>
);

// ---------- Today's Route (right-sidebar) -----------------------------------
// Compact, course-aware itinerary. Steps that match a block found in today's
// lesson are highlighted; the rest are shown muted so the child sees the
// shape of the day before diving in.
const TodaysRouteCard = ({ t, language, courseTemplate, blocks, activeTab, onJumpOnline }) => {
  const steps = buildTodaysRoute(courseTemplate, blocks);
  return (
    <Card className="p-4" data-testid="lesson-todays-route">
      <h3 className="text-amber-200/75 text-xs uppercase tracking-wider font-sans mb-3">
        {language === 'ru' ? 'Маршрут дня' : "Today's route"}
      </h3>
      <ol className="space-y-1.5" data-testid="lesson-todays-route-list">
        {steps.map((step, i) => (
          <li
            key={step.id}
            className={`flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm font-sans ${
              step.active
                ? 'text-[#f5ebe0] bg-amber-300/8 border border-amber-300/25'
                : 'text-amber-200/40 border border-transparent'
            }`}
            data-testid={`lesson-todays-route-step-${step.id}`}
          >
            <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-sans border ${
              step.active
                ? 'border-amber-300/45 bg-amber-300/15 text-[#f3d9a4]'
                : 'border-amber-300/15 text-amber-200/45'
            }`}>
              {i + 1}
            </span>
            <span className="flex-1 truncate">
              {language === 'ru' ? step.ru : step.en}
            </span>
          </li>
        ))}
      </ol>
      {activeTab !== 'online' && (
        <button
          type="button"
          onClick={onJumpOnline}
          className="mt-3 w-full px-3 py-2 rounded-xl border border-amber-300/25 bg-amber-300/5 text-amber-200/85 hover:bg-amber-300/10 text-sm font-sans"
          data-testid="lesson-todays-route-open-online"
        >
          {language === 'ru' ? 'Открыть миссию' : 'Open mission'}
        </button>
      )}
    </Card>
  );
};

// ---------- Sidebar quick actions -------------------------------------------
const SidebarActionsCard = ({ t, language, base, weekId, dayId, tab, setTab, isParent }) => (
  <Card className="p-3" data-testid="lesson-sidebar-actions">
    <button
      type="button"
      onClick={() => setTab('notebook')}
      className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-sans transition-colors ${
        tab === 'notebook'
          ? 'border border-amber-300/35 bg-amber-300/10 text-[#f5ebe0]'
          : 'border border-amber-300/15 bg-[#0a0f26]/60 text-[#f5ebe0]/85 hover:bg-amber-300/5'
      }`}
      data-testid="lesson-sidebar-jump-notebook"
    >
      <NotebookPen className="w-4 h-4 text-amber-200/70" />
      {t('lessonNotebookMode')}
    </button>
    <Link
      to={`${base}/${weekId}/${dayId}/print`}
      className="mt-2 w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-sans border border-amber-300/15 bg-[#0a0f26]/60 text-[#f5ebe0]/85 hover:bg-amber-300/5 transition-colors"
      data-testid="lesson-sidebar-jump-print"
    >
      <Printer className="w-4 h-4 text-amber-200/70" />
      {t('lessonPrintMode')}
    </Link>
    <button
      type="button"
      onClick={() => setTab('parent')}
      className={`mt-2 w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-sans transition-colors ${
        tab === 'parent'
          ? 'border border-amber-300/35 bg-amber-300/10 text-[#f5ebe0]'
          : 'border border-amber-300/15 bg-[#0a0f26]/60 text-[#f5ebe0]/85 hover:bg-amber-300/5'
      }`}
      data-testid="lesson-sidebar-jump-parent"
    >
      <Users className="w-4 h-4 text-amber-200/70" />
      {t('lessonParentMode')}
    </button>
    {!isParent && (
      <p className="mt-2 text-amber-200/45 text-[11px] font-sans px-1 leading-tight">
        {language === 'ru'
          ? 'Гид родителя — только для родителя.'
          : 'Parent guide is parent-only.'}
      </p>
    )}
  </Card>
);

// ---------- Optional extended teaching guidance (Phase 0 additive) ----------
// Renders any subset of the new parentGuide fields supplied by a lesson.
// Max lessons currently omit these → component renders nothing.
const PARENT_GUIDE_OPTIONAL_FIELDS = [
  { key: 'whatThisLessonTeaches', ru: 'Чему учит этот урок',         en: 'What this lesson teaches' },
  { key: 'keyMessage',            ru: 'Главная мысль',                en: 'Key message' },
  { key: 'howToExplain',          ru: 'Как объяснить ребёнку',        en: 'How to explain' },
  { key: 'exampleDialogue',       ru: 'Пример диалога',               en: 'Example dialogue' },
  { key: 'readingSupport',        ru: 'Помощь с чтением',             en: 'Reading support' },
  { key: 'russianSpeechGoal',     ru: 'Цель по русской речи',         en: 'Russian speech goal' },
  { key: 'mathAnswerKey',         ru: 'Ответы (математика)',          en: 'Math answer key' },
  { key: 'spellingAnswerKey',     ru: 'Ответы (правописание)',        en: 'Spelling answer key' },
  { key: 'drawingCriteria',       ru: 'Критерии для рисунка',         en: 'Drawing criteria' },
  { key: 'projectCriteria',       ru: 'Критерии проекта',             en: 'Project criteria' },
  { key: 'commonMistakes',        ru: 'Частые ошибки',                en: 'Common mistakes' },
  { key: 'rewardGuidance',        ru: 'Подсказка к награде',          en: 'Reward guidance' },
  { key: 'parentOnlyNotes',       ru: 'Заметки только для родителя',  en: 'Parent-only notes' },
  { key: 'safetyNotes',           ru: 'Безопасность',                 en: 'Safety notes' },
  { key: 'financeSafetyNotes',    ru: 'Финансовая безопасность',      en: 'Finance safety notes' },
  { key: 'rubric',                ru: 'Рубрика оценки',                en: 'Grading rubric' }
];

const OptionalFieldBody = ({ value, tx }) => {
  const resolved = tx(value);
  if (resolved == null || resolved === '') {
    // tx returned '' (plain-string field). Fall back to the raw value if it
    // is a usable string or array, so additive content from new schemas
    // still renders.
    if (typeof value === 'string' && value.trim()) {
      return (
        <p className="text-amber-100/85 font-serif text-sm leading-relaxed">
          {value}
        </p>
      );
    }
    return null;
  }
  if (Array.isArray(resolved)) {
    if (!resolved.length) return null;
    return (
      <ul className="space-y-1.5 list-disc list-inside text-amber-100/85 font-serif text-sm">
        {resolved.map((line, i) => (
          <li key={i}>{typeof line === 'string' ? line : tx(line)}</li>
        ))}
      </ul>
    );
  }
  return (
    <p className="text-amber-100/85 font-serif text-sm leading-relaxed">
      {String(resolved)}
    </p>
  );
};

const ParentGuideOptional = ({ pg, tx, language }) => {
  if (!pg) return null;
  const present = PARENT_GUIDE_OPTIONAL_FIELDS.filter((f) => pg[f.key] != null);
  if (!present.length) return null;
  return (
    <div
      className="mt-5 pt-4 border-t border-amber-300/10 space-y-4"
      data-testid="parent-guide-optional"
    >
      {present.map((f) => (
        <div key={f.key} data-testid={`parent-guide-${f.key}`}>
          <h3 className="text-amber-200/75 font-sans text-sm uppercase tracking-wider mb-2">
            {language === 'ru' ? f.ru : f.en}
          </h3>
          <OptionalFieldBody value={pg[f.key]} tx={tx} />
        </div>
      ))}
    </div>
  );
};

// ---------- Parent Theory Card (Week 2 safety pedagogy) -------------------
// Renders the structured `parentGuide.parentTheoryCard` object as a dedicated
// dark card inside the parent-only tab. The component returns null for any
// lesson that does not supply the field (Anita Week 1, Max lessons) so the
// existing UI is unchanged for those days.
//
// SAFETY: this component is mounted only inside the parent tab block, which
// is gated by `isParent` higher in Lesson.jsx. PrintWorksheet renders solely
// from `day.print.tasks`, so the card never appears on the child print.
const PARENT_THEORY_CARD_ROWS = [
  { key: 'purpose',        ru: 'Зачем нужен этот разговор', en: 'Why this conversation matters' },
  { key: 'keyMessage',     ru: 'Главная мысль',              en: 'Key message' },
  { key: 'parentScript',   ru: 'Что сказать ребёнку',        en: 'What to say to your child' },
  { key: 'practiceSteps',  ru: 'Как потренировать',          en: 'How to practice' },
  { key: 'doNotSay',       ru: 'Чего не говорить',           en: 'What NOT to say' },
  { key: 'paperOnlyNotes', ru: 'Только на бумаге дома',      en: 'Paper-only at home' }
];

const TheoryCardRow = ({ value, language, testid }) => {
  if (value == null) return null;
  if (Array.isArray(value)) {
    if (value.length === 0) return null;
    return (
      <ul
        className="space-y-1.5 list-disc list-inside text-amber-100/85 font-serif text-sm"
        data-testid={testid}
      >
        {value.map((line, i) => (
          <li key={i}>{String(line)}</li>
        ))}
      </ul>
    );
  }
  const text = String(value).trim();
  if (!text) return null;
  return (
    <p
      className="text-amber-100/85 font-serif text-sm leading-relaxed whitespace-pre-line"
      data-testid={testid}
    >
      {text}
    </p>
  );
};

const ParentTheoryCardSection = ({ ptc, language }) => {
  if (!ptc || typeof ptc !== 'object') return null;
  const title = ptc.title ? String(ptc.title).trim() : '';
  return (
    <div
      className="mt-5 pt-4 border-t border-amber-300/10"
      data-testid="parent-theory-card"
    >
      <div className="rounded-2xl border border-amber-300/25 bg-amber-300/5 p-4">
        <div className="flex items-center gap-2 mb-3">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-amber-300/30 bg-[#0a0f26]/60 text-amber-200 text-[10px] uppercase tracking-wider font-sans">
            {language === 'ru' ? 'Только для родителя' : 'Parent only'}
          </span>
          <span className="text-amber-200/55 font-sans text-xs">
            {language === 'ru' ? 'Материал для родителя / Parent Theory Card' : 'Parent Theory Card / Материал для родителя'}
          </span>
        </div>
        {title && (
          <h3
            className="text-[#f3d9a4] text-lg mb-3"
            style={{ fontFamily: 'var(--font-heading)' }}
            data-testid="parent-theory-card-title"
          >
            {title}
          </h3>
        )}
        <div className="space-y-4">
          {PARENT_THEORY_CARD_ROWS.map((row) => {
            const v = ptc[row.key];
            if (v == null) return null;
            if (Array.isArray(v) && v.length === 0) return null;
            if (typeof v === 'string' && !v.trim()) return null;
            return (
              <div key={row.key} data-testid={`parent-theory-card-${row.key}`}>
                <h4 className="text-amber-200/75 font-sans text-xs uppercase tracking-wider mb-1.5">
                  {language === 'ru' ? row.ru : row.en}
                </h4>
                <TheoryCardRow value={v} language={language} />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Lesson;
